import { AlertTriangle, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { isApiError } from '@/lib/api';
import { cn } from '@/lib/utils';

export interface ErrorStateProps {
  error: unknown;
  onRetry?: () => void;
  className?: string;
}

/**
 * A failed read: "You don't have access to this" for 403, "This was removed" for 410 (with the
 * server's removal message, for example "Attachment removed under the retention policy on <date>."),
 * the server's friendly detail otherwise.
 */
export function ErrorState({ error, onRetry, className }: ErrorStateProps) {
  const forbidden = isApiError(error) && error.status === 403;
  const notFound = isApiError(error) && error.status === 404;
  const gone = isApiError(error) && error.status === 410;
  const title = forbidden
    ? 'You don’t have access to this'
    : notFound
      ? 'We couldn’t find that'
      : gone
        ? 'This was removed'
        : 'Something went wrong';
  const detail = isApiError(error)
    ? error.detail
    : 'The page couldn’t load. Try again, and if it keeps happening, tell the Platform admins.';
  const Icon = forbidden ? Lock : AlertTriangle;
  return (
    <div
      role="alert"
      className={cn('flex flex-col items-center gap-2 rounded-lg border border-border p-8 text-center', className)}
    >
      <Icon className={cn('size-8', forbidden ? 'text-muted' : 'text-danger')} aria-hidden="true" />
      <p className="font-medium text-text">{title}</p>
      <p className="max-w-md text-sm text-muted">{detail}</p>
      {onRetry && !forbidden && !notFound && !gone ? (
        <Button variant="secondary" size="sm" onClick={onRetry} className="mt-2">
          Try again
        </Button>
      ) : null}
    </div>
  );
}
